// =====================================================================
// /admin/users - Gestionare utilizatori (Faza 1)
// =====================================================================
// Functionalitati:
// - Listare cu filtre (rol, grup, activ) + search
// - Creare user nou (modal)
// - Editare user (modal cu optimistic locking)
// - Toggle active/inactive (confirm popup)
// - Conflict 409 -> modal cu 3 optiuni (Refresh / Suprascrie / Anuleaza)
// =====================================================================

import { useEffect, useState, useMemo } from 'react';
import { users as usersApi, groups as groupsApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import GroupsTab from './GroupsTab.jsx';
import { DROITS_VOIR_EQUIPES, DROITS_GERER_EQUIPES } from '../../lib/droits-equipes.js';
import { CHAMPS_DOSSIER } from '../../lib/droits-dossier.js';
import { ROLES, ROLE_LABELS, ficheReservee } from '../../lib/roles.js';
import { nomAEnregistrer, nomsModifies, messageNomIncoherent, nomEnMajuscules, prenomEnForme } from '../../lib/noms.js';
import InvitationsTab from './InvitationsTab.jsx';   // M-011 (26-08-2026) — Onboarding contrats par QR


// Avatare cu gradient pseudo-random pe baza ID-ului
const AVATAR_GRADIENTS = [
  ['#6366f1', '#8b5cf6'],
  ['#ec4899', '#f43f5e'],
  ['#06b6d4', '#3b82f6'],
  ['#10b981', '#14b8a6'],
  ['#f59e0b', '#ef4444'],
  ['#8b5cf6', '#d946ef'],
  ['#0ea5e9', '#22d3ee'],
];

function getInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getGradient(id) {
  let hash = 0;
  for (let i = 0; i < (id || '').length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

export default function AdminUsersPage() {
  const { can, user: currentUser } = useAuth();
  // Droit sur les COMPTES (sinon : page limitée à l'onglet Groupes). Calculé une fois.
  const canAdminUsers = can('users.create.all') || can('users.update.all');
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filtre
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  
  // Modale
  const [editingUser, setEditingUser] = useState(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  // Main tabs (Day 9 — Groups parametrization)
  // Sans droit sur les comptes, on ouvre directement sur Groupes (pas d'écran vide).
  const [activeMainTab, setActiveMainTab] = useState(() => (canAdminUsers ? 'users' : 'groups')); // 'users' | 'groups' | 'invites'

  // Permisiuni
  const canCreate = can('users.create.all');
  const canUpdate = can('users.update.all');
  const canToggle = can('users.toggle_active.all');

  const loadUsers = async () => {
    // Pas de liste des comptes pour qui n'a que les droits « équipes » : elle
    // serait refusée (403) et afficherait une erreur sans objet.
    if (!canAdminUsers) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = {};
      if (filterRole) params.role = filterRole;
      if (filterGroup) params.groupId = filterGroup;
      if (filterActive !== 'all') params.active = filterActive;
      if (search.trim()) params.search = search.trim();
      // Seul écran à demander le dossier : la fiche s'ouvre avec ces valeurs et les
      // renvoie — sans elles, un enregistrement effacerait IBAN, NISS, adresse…
      params.champs = 'dossier';
      const data = await usersApi.list(params);
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [filterRole, filterGroup, filterActive]);


  // Search cu debounce 300ms
  useEffect(() => {
    const t = setTimeout(loadUsers, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    groupsApi.list().then(setGroups).catch(() => setGroups([]));
  }, []);

  // Day 9d — password reset (per user)
  const [pwdResetResult, setPwdResetResult] = useState(null); // { userName, tempPassword } | { bulk: [...] }

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${user.name} ?\n\nUn mot de passe temporaire sera généré. L'utilisateur devra le changer à sa prochaine connexion.`)) return;
    try {
      const result = await usersApi.resetPassword(user.id);
      setPwdResetResult({ single: result });
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleBulkReset = async () => {
    if (!window.confirm(`Réinitialiser le mot de passe de TOUS les utilisateurs actifs SAUF VOUS ?\n\n${users.filter(u => u.isActive && u.id !== currentUser?.id).length} utilisateurs seront affectés.\n\nLes mots de passe temporaires seront affichés une seule fois.`)) return;
    try {
      const result = await usersApi.resetPasswordBulk([currentUser?.id].filter(Boolean));
      setPwdResetResult({ bulk: result });
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleToggleActive = async (user) => {
    const action = user.isActive ? 'desactiver' : 'activer';
    if (!window.confirm(`Voulez-vous ${action} ${user.name} ?`)) return;
    
    try {
      await usersApi.toggleActive(user.id, user.updatedAt);
      await loadUsers();
    } catch (err) {
      if (err.response?.status === 409) {
        setConflictData({
          type: 'toggle',
          user,
          serverData: err.response.data.details?.currentData,
        });
      } else {
        alert(err.response?.data?.message || 'Erreur');
      }
    }
  };

  // Top-level gate: only users with admin-level perm see this page
  // 05-10-2026 : qui GÈRE les équipes (groups.create / groups.update) ouvre la page,
  // sur l'onglet Groupes seulement. Sans ça, groups.update.all ne servait à rien.
  if (!canAdminUsers && !can(DROITS_GERER_EQUIPES)) {
    return (
      <div style={{ padding: 40, color: '#991b1b', background: '#fee2e2', borderRadius: 8, margin: 20 }}>
        <h2 style={{ marginTop: 0 }}>Accès refusé</h2>
        <p>Cette page est réservée à l'administration.</p>
      </div>
    );
  }

  return (
    <div className="admin-users">
      {/* Main tabs (Day 9): Utilisateurs / Groupes — gated separately */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: 16, marginTop: 4 }}>
        {canAdminUsers && <button
          onClick={() => setActiveMainTab('users')}
          style={{
            padding: '10px 18px', background: 'transparent', border: 0,
            borderBottom: '2px solid ' + (activeMainTab === 'users' ? '#2563eb' : 'transparent'),
            marginBottom: -2, color: activeMainTab === 'users' ? '#2563eb' : '#6b7280',
            fontWeight: activeMainTab === 'users' ? 600 : 400, cursor: 'pointer', fontSize: 14,
          }}
        >👥 Utilisateurs ({users.length})</button>}
        {can(DROITS_VOIR_EQUIPES) && (
          <button
            onClick={() => setActiveMainTab('groups')}
            style={{
              padding: '10px 18px', background: 'transparent', border: 0,
              borderBottom: '2px solid ' + (activeMainTab === 'groups' ? '#2563eb' : 'transparent'),
              marginBottom: -2, color: activeMainTab === 'groups' ? '#2563eb' : '#6b7280',
              fontWeight: activeMainTab === 'groups' ? 600 : 400, cursor: 'pointer', fontSize: 14,
            }}
          >🏷️ Groupes ({groups.length})</button>
        )}
        {can('users.create.all') && (
          <button
            onClick={() => setActiveMainTab('invites')}
            style={{
              padding: '10px 18px', background: 'transparent', border: 0,
              borderBottom: '2px solid ' + (activeMainTab === 'invites' ? '#2563eb' : 'transparent'),
              marginBottom: -2, color: activeMainTab === 'invites' ? '#2563eb' : '#6b7280',
              fontWeight: activeMainTab === 'invites' ? 600 : 400, cursor: 'pointer', fontSize: 14,
            }}
          >📨 Invitations</button>
        )}
      </div>

      {activeMainTab === 'invites' && can('users.create.all') ? (
        <InvitationsTab groups={groups} />
      ) : activeMainTab === 'groups' && can(DROITS_VOIR_EQUIPES) ? (
        <GroupsTab />
      ) : (activeMainTab === 'groups' || activeMainTab === 'invites') ? (
        <div style={{ padding: 24 }}>Accès refusé.</div>
      ) : (
        <UsersContent
          users={users}
          groups={groups}
          loading={loading}
          error={error}
          search={search} setSearch={setSearch}
          filterRole={filterRole} setFilterRole={setFilterRole}
          filterGroup={filterGroup} setFilterGroup={setFilterGroup}
          filterActive={filterActive} setFilterActive={setFilterActive}
          canCreate={canCreate} canUpdate={canUpdate} canToggle={canToggle}
          setCreatingUser={setCreatingUser} setEditingUser={setEditingUser}
          loadUsers={loadUsers} setConflictData={setConflictData}
          currentUser={currentUser} handleToggleActive={handleToggleActive}
          handleResetPassword={handleResetPassword} handleBulkReset={handleBulkReset}
        />
      )}

      {pwdResetResult && (
        <PasswordResetResultModal data={pwdResetResult} onClose={() => setPwdResetResult(null)} />
      )}

      {creatingUser && (
        <UserModal
          mode="create"
          groups={groups}
          onClose={() => setCreatingUser(false)}
          onSaved={() => { setCreatingUser(false); loadUsers(); }}
        />
      )}

      {editingUser && (
        <UserModal
          mode="edit"
          user={editingUser}
          groups={groups}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); loadUsers(); }}
          onConflict={(data) => { setEditingUser(null); setConflictData(data); }}
        />
      )}

      {conflictData && (
        <ConflictModal
          data={conflictData}
          onClose={() => setConflictData(null)}
          onResolved={() => { setConflictData(null); loadUsers(); }}
        />
      )}
    </div>
  );
}

// =====================================================================
// UsersContent — extracted body of the "Utilisateurs" tab (Day 9)
// =====================================================================
function UsersContent({
  users, groups, loading, error,
  search, setSearch, filterRole, setFilterRole, filterGroup, setFilterGroup, filterActive, setFilterActive,
  canCreate, canUpdate, canToggle, setCreatingUser, setEditingUser, loadUsers, setConflictData,
  currentUser, handleToggleActive, handleResetPassword, handleBulkReset,
}) {
  return (
    <>
      <div className="page-header">
        <h1>
          Utilisateurs <span className="count-badge">{users.length}</span>
        </h1>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          {canUpdate && (
            <button onClick={handleBulkReset} style={{ padding: '6px 12px', background: 'white', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }} title="Réinitialiser le mot de passe de tous les utilisateurs sauf vous-même">
              🔑 Reset mdp (tous)
            </button>
          )}
          {canCreate && (
            <button className="btn btn-primary" onClick={() => setCreatingUser(true)}>
              + Nouvel utilisateur
            </button>
          )}
        </div>
      </div>

      <div className="toolbar">
        <input
          type="text"
          placeholder="🔍 Rechercher par nom, username ou email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="">Tous les rôles</option>
          {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
        <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}>
          <option value="">Tous les groupes</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <select value={filterActive} onChange={e => setFilterActive(e.target.value)}>
          <option value="all">Tous les statuts</option>
          <option value="true">Actifs uniquement</option>
          <option value="false">Inactifs uniquement</option>
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-wrapper">
        {loading ? (
          <div className="loading">Chargement...</div>
        ) : users.length === 0 ? (
          <div className="empty">Aucun utilisateur trouvé</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Rôle</th>
                <th>Groupe</th>
                <th>Statut</th>
                {(canUpdate || canToggle) && <th></th>}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const [c1, c2] = getGradient(u.id);
                return (
                  <tr key={u.id} className={u.isActive ? '' : 'row-inactive'}>
                    <td>
                      <div className="name-cell">
                        <span
                          className="avatar"
                          style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}
                        >
                          {getInitials(u.name)}
                        </span>
                        <div>
                          <div className="name">{u.name}</div>
                          <div className="username">{u.username}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${u.role.toLowerCase().replace('_', '-')}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td>
                      {u.group ? <span className="group-tag">{u.group.name}</span> : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <span className={`status status-${u.isActive ? 'active' : 'inactive'}`}>
                        <span className="status-dot"></span>
                        {u.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    {(canUpdate || canToggle) && (
                      <td className="actions">
                        {/* Fiche d'un accès total : actions réservées à un accès total (lib/roles.js). */}
                        {canUpdate && !ficheReservee(u, currentUser) && (
                          <button className="btn-icon" onClick={() => setEditingUser(u)} title="Modifier">
                            ✏️
                          </button>
                        )}
                        {canUpdate && u.id !== currentUser?.id && !ficheReservee(u, currentUser) && (
                          <button
                            className="btn-icon"
                            onClick={() => handleResetPassword(u)}
                            title="Réinitialiser le mot de passe"
                          >
                            🔑
                          </button>
                        )}
                        {/* Désactiver un accès total actif : réservé (le serveur refuse) ; le réactiver reste possible. */}
                        {canToggle && u.id !== currentUser?.id && !(u.isActive && ficheReservee(u, currentUser)) && (
                          <button
                            className="btn-icon"
                            onClick={() => handleToggleActive(u)}
                            title={u.isActive ? 'Désactiver' : 'Activer'}
                          >
                            {u.isActive ? '🚫' : '✅'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// =====================================================================
// Modal Create/Edit User
// =====================================================================
function UserModal({ mode, user, groups, onClose, onSaved, onConflict }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({
    username: user?.username || '',
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'OPERATOR',
    groupId: user?.groupId || '',
    contractHoursWeek: user?.contractHoursWeek ?? 38,
    contractType: user?.contractType || 'CDI',
    contractStart: user?.contractStart || '',
    contractEnd: user?.contractEnd || '',
    address: user?.address || '',
    iban: user?.iban || '',
    birthPlace: user?.birthPlace || '',   // M-011 (26-08-2026) — demande Aurélie
    // 30-09-2026 (décision Silviu) — la fiche n'envoie la demande de contrat que si
    // on coche. Toujours décoché à l'ouverture, même en édition : corriger un nom ou
    // rattraper une faute de frappe sur une date ne doit pas partir chez SD Worx.
    sendContractRequest: false,
    // Dossier SD Worx (30-09-2026) — corrigeables ici (ex. NISS mal lu sur la photo)
    lastName: user?.lastName || '',
    firstName: user?.firstName || '',
    idCardNumber: user?.idCardNumber || '',
    niss: user?.niss || '',
    nationality: user?.nationality || '',
    gender: user?.gender || '',
    language: user?.language || '',
    disabledWorker: user?.disabledWorker ?? '',
    survivorPension: user?.survivorPension ?? '',
    spouseBirthDate: user?.spouseBirthDate || '',
    spouseIncome: user?.spouseIncome || '',
    // Compléments dossier (19-08-2026, demande Florent)
    dependentsCount: user?.dependentsCount ?? '',
    maritalStatus: user?.maritalStatus || '',
    spouseName: user?.spouseName || '',
    spouseWorks: user?.spouseWorks ?? '',
    motif: '',
    password: 'test1234',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  // Photos carte d'identité (19-08-2026) — uploadées après la sauvegarde, jointes au mail contrats
  const [ciRecto, setCiRecto] = useState(null);
  const [ciVerso, setCiVerso] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    
    // CDD/Intérim : période obligatoire (contrôle client — le backend revalide)
    // NISS : 11 chiffres. Sans ce contrôle, une faute de frappe partait telle quelle
    // dans le mail SD Worx et l'erreur ne se voyait que chez eux (Aurélie 30-09-2026).
    const nissDigits = (form.niss || '').replace(/\D/g, '');
    if (nissDigits && nissDigits.length !== 11) {
      setErr(`N° NISS : 11 chiffres attendus (${nissDigits.length} saisis).`);
      setSaving(false);
      return;
    }
    if (form.contractType !== 'CDI' && (!form.contractStart || !form.contractEnd || form.contractStart > form.contractEnd)) {
      setErr('CDD/Intérim : indiquez la période de contrat (date début ≤ date fin).');
      setSaving(false);
      return;
    }
    // Même règle que le serveur (lib/noms.js), dite AVANT l'envoi.
    const nomFiche = nomAEnregistrer(form.lastName, form.firstName, form.name);
    if (!nomFiche.nom) {
      setErr('Indiquez le Nom et le Prénom (ou, pour un compte technique, le nom affiché).');
      setSaving(false);
      return;
    }
    // Une fiche ANCIENNE déjà incohérente, enregistrée pour autre chose (contrat,
    // IBAN), passe : le serveur la laisse passer aussi. Seul un nom modifié est vérifié.
    // Même test et même message que le serveur (lib/noms.js).
    const nomsChanges = !isEdit || nomsModifies(user, { name: form.name, lastName: form.lastName, firstName: form.firstName });
    if (nomFiche.incoherent && nomsChanges) {
      setErr(messageNomIncoherent(form.name, form.lastName, form.firstName));
      setSaving(false);
      return;
    }
    const contractFields = {
      contractType: form.contractType,
      contractStart: form.contractType !== 'CDI' ? form.contractStart : null,
      contractEnd: form.contractType !== 'CDI' ? form.contractEnd : null,
      address: form.address?.trim() || null,
      iban: form.iban?.trim() || null,
      birthPlace: form.birthPlace?.trim() || null,
      // NOM en majuscules / Prénom capitalisé : même règle que le formulaire public
      lastName: nomEnMajuscules(form.lastName) || null,      // lib/noms.js, comme le serveur
      firstName: prenomEnForme(form.firstName) || null,
      idCardNumber: form.idCardNumber?.trim() || null,
      niss: form.niss?.replace(/\D/g, '') || null,
      nationality: form.nationality?.trim() || null,
      gender: form.gender || null,
      language: form.language || null,
      disabledWorker: form.disabledWorker === '' ? null : form.disabledWorker === true || form.disabledWorker === 'true',
      survivorPension: form.survivorPension === '' ? null : form.survivorPension === true || form.survivorPension === 'true',
      spouseBirthDate: (form.maritalStatus === 'MARIE' || form.maritalStatus === 'COHABITANT_LEGAL') ? (form.spouseBirthDate || null) : null,
      spouseIncome: (form.maritalStatus === 'MARIE' || form.maritalStatus === 'COHABITANT_LEGAL') ? (form.spouseIncome?.trim() || null) : null,
      dependentsCount: form.dependentsCount === '' ? null : Number(form.dependentsCount),
      maritalStatus: form.maritalStatus || null,
      spouseName: (form.maritalStatus === 'MARIE' || form.maritalStatus === 'COHABITANT_LEGAL') ? (form.spouseName?.trim() || null) : null,
      spouseWorks: (form.maritalStatus === 'MARIE' || form.maritalStatus === 'COHABITANT_LEGAL') && form.spouseWorks !== '' ? form.spouseWorks === true || form.spouseWorks === 'true' : null,
    };
    // Férié dans la période ? → proposer la découpe.
    // 30-09-2026 : s'applique aussi à l'ÉDITION d'un collaborateur déjà connu.
    // Avant, la condition « !isEdit » sautait ce contrôle ET le motif lors d'une
    // prolongation — la demande SD Worx ne partait donc jamais depuis Zeus et les
    // prolongations se faisaient à côté du système.
    // Garde : sur édition, uniquement si la PÉRIODE change réellement (sinon une
    // simple correction de nom ouvrirait une confirmation de férié).
    const periodChanged = !isEdit
      || form.contractStart !== (user?.contractStart || '')
      || form.contractEnd !== (user?.contractEnd || '');
    if (form.contractType === 'CDD' && periodChanged) {
      try {
        const chk = await usersApi.contractFerieCheck(form.contractStart, form.contractEnd);
        if (chk.feries?.length) {
          const fList = chk.feries.map(f => `${f.name} (${f.date})`).join(', ');
          const segTxt = (chk.split || []).map(s => `${s.start} → ${s.end}`).join('  +  ');
          contractFields.splitAroundFerie = window.confirm(
            `⚠️ La période contient un jour férié : ${fList}.\n\nScinder en ${chk.split?.length || 2} contrats pour ne pas payer le férié (~200 €) ?\n${segTxt}\n\nOK = scinder · Annuler = 1 seul contrat (férié payé)`
          );
        }
      } catch (_) { /* best-effort */ }
      if (form.motif?.trim()) contractFields.motif = form.motif.trim();
      // Le backend n'envoie le mail QUE sur true explicite (voir user.service.js).
      // À la CRÉATION on ne l'envoie pas : create() met la demande en file dans tous
      // les cas (un nouveau CDD a forcément besoin d'un contrat), donc une case ici
      // serait un mensonge — elle n'est affichée qu'en édition.
      if (isEdit) contractFields.sendContractRequest = form.sendContractRequest === true;
    }
    let payloadEdition = null;   // renvoyé tel quel par « Écraser » en cas de conflit (409)
    try {
      let savedId = user?.id;
      if (isEdit) {
        const payload = {
          username: form.username,
          name: nomFiche.nom,
          email: form.email || null,
          // Rôle envoyé seulement s'il change : la fiche d'un accès total (rôle
          // absent de la liste attribuable) était refusée à chaque enregistrement.
          ...(form.role !== user.role ? { role: form.role } : {}),
          groupId: form.groupId || null,
          contractHoursWeek: parseFloat(form.contractHoursWeek),
          ...contractFields,
          // La fiche a bien été ouverte AVEC son dossier : le serveur peut l'écrire.
          dossierCharge: Object.prototype.hasOwnProperty.call(user, 'iban'),
          expectedUpdatedAt: user.updatedAt,
        };
        // Seuls les champs du dossier MODIFIÉS partent : un champ resté vide par accident
        // ne peut pas effacer IBAN / NISS (le serveur le vérifie aussi : dossierCharge).
        for (const k of CHAMPS_DOSSIER) {
          if (k in payload && String(payload[k] ?? '') === String(user[k] ?? '')) delete payload[k];
        }
        payloadEdition = payload;
        const updated = await usersApi.update(user.id, payload);
        // Le serveur rétablit les droits de base perdus par un changement de
        // rôle ou de groupe. Quand il n'y arrive pas, ou quand le compte reste
        // sans ces droits, il le dit — encore faut-il l'afficher, sinon
        // l'écran annonce un succès sur un compte devenu muet.
        if (updated?.warning) alert(updated.warning);
      } else {
        const payload = {
          username: form.username,
          password: form.password,
          name: nomFiche.nom,
          email: form.email || null,
          role: form.role,
          groupId: form.groupId || null,
          contractHoursWeek: parseFloat(form.contractHoursWeek),
          ...contractFields,
        };
        const created = await usersApi.create(payload);
        savedId = created?.id;
      }
      // Upload photos CI (best-effort — le mail contrats les joint au moment de l'envoi)
      if (savedId && (ciRecto || ciVerso)) {
        try {
          if (ciRecto) await usersApi.uploadCi(savedId, 'recto', ciRecto);
          if (ciVerso) await usersApi.uploadCi(savedId, 'verso', ciVerso);
        } catch (upErr) {
          alert('Utilisateur sauvegardé, mais échec de l\'upload de la carte d\'identité : '
            + (upErr.response?.data?.message || upErr.message) + '\nVous pouvez réessayer en modifiant l\'utilisateur.');
        }
      }
      onSaved();
    } catch (error) {
      if (error.response?.status === 409 && error.response.data.error === 'CONFLICT_VERSION') {
        // Optimistic locking conflict
        if (onConflict) {
          onConflict({
            type: 'edit',
            user,
            myChanges: payloadEdition,
            serverData: error.response.data.details?.currentData,
          });
          return;
        }
      }
      setErr(error.response?.data?.message || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Modifier utilisateur' : 'Nouvel utilisateur'}</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {err && <div className="alert alert-error">{err}</div>}
            

            <div className="form-row">
              <label>Username *</label>
              <input
                type="text"
                required
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
              />
            </div>
            
            {/* 05-10-2026 : Nom et Prénom pour tout le monde (ils n'apparaissaient
                que pour un CDD). Le nom affiché en est DÉDUIT — « NOM Prénom » — dès
                que les deux sont remplis : on ne peut plus en saisir un qui les
                contredit. Le serveur applique la même règle et fait foi. */}
            <div className="form-row" style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label>Nom <small style={{ color: '#94a3b8' }}>(de famille)</small></label>
                <input type="text" value={form.lastName} maxLength={100} style={{ textTransform: 'uppercase' }}
                  onChange={e => setForm({ ...form, lastName: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label>Prénom</label>
                <input type="text" value={form.firstName} maxLength={100}
                  onChange={e => setForm({ ...form, firstName: e.target.value })} />
              </div>
            </div>

            <div className="form-row">
              <label>Nom affiché *</label>
              {/* Nom ET Prénom → calculé, non modifiable. Un seul des deux → libre, mais
                  il doit le contenir (« MARTIN » / « Jean Dupont » refusé). Même règle
                  que le serveur : lib/noms.js. */}
              {(() => {
                const aNom = !!(form.lastName || '').trim(), aPrenom = !!(form.firstName || '').trim();
                const fiche = nomAEnregistrer(form.lastName, form.firstName, form.name);
                if (aNom && aPrenom) return (
                  <>
                    <input type="text" value={fiche.nom} readOnly
                      style={{ background: '#f1f5f9', color: '#334155' }} aria-describedby="aide-nom-affiche" />
                    <small id="aide-nom-affiche" style={{ color: '#64748b' }}>Calculé à partir du Nom et du Prénom (« NOM Prénom »).</small>
                  </>
                );
                return (
                  <>
                    <input type="text" required={!aNom && !aPrenom} value={form.name || ''}
                      placeholder={nomAEnregistrer(form.lastName, form.firstName, '').nom || ''}
                      onChange={e => setForm({ ...form, name: e.target.value })} aria-describedby="aide-nom-affiche" />
                    {fiche.incoherent ? (
                      <small id="aide-nom-affiche" style={{ color: '#b91c1c' }}>
                        Ne contient pas {aNom ? `le Nom « ${form.lastName.trim()} »` : `le Prénom « ${form.firstName.trim()} »`} : corrigez-le, ou remplissez aussi {aNom ? 'le Prénom' : 'le Nom'}.
                      </small>
                    ) : (
                      <small id="aide-nom-affiche" style={{ color: '#64748b' }}>
                        {aNom || aPrenom
                          ? `Remplissez aussi le ${aNom ? 'Prénom' : 'Nom'} : il sera calculé tout seul. Laissé vide, on garde la partie connue.`
                          : 'Sans Nom ni Prénom (compte technique), écrivez-le ici. Sinon remplissez-les : il sera calculé tout seul.'}
                      </small>
                    )}
                  </>
                );
              })()}
            </div>
            
            <div className="form-row">
              <label>Email</label>
              <input
                type="email"
                value={form.email || ''}
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
            </div>
            
            <div className="form-row">
              <label>Rôle *</label>
              <select
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}
              >
                {/* Un accès total n'est pas attribuable ici, mais sa fiche doit
                    s'afficher juste et pouvoir s'enregistrer (Nom, Prénom…). L'option
                    reste choisissable pour revenir en arrière après une fausse
                    manœuvre (le rôle n'est envoyé que s'il change). */}
                {user?.role === 'SUPER_ADMIN' && <option value="SUPER_ADMIN">{ROLE_LABELS.SUPER_ADMIN}</option>}
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            
            <div className="form-row">
              <label>Groupe</label>
              <select
                value={form.groupId}
                onChange={e => setForm({ ...form, groupId: e.target.value })}
              >
                <option value="">— Aucun —</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            
            <div className="form-row">
              <label>Heures contrat / semaine</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="60"
                value={form.contractHoursWeek}
                onChange={e => setForm({ ...form, contractHoursWeek: e.target.value })}
              />
            </div>

            {/* Contrats (17-08-2026) : CDI/CDD + période obligatoire pour CDD */}
            <div className="form-row">
              <label>Type de contrat *</label>
              <select
                value={form.contractType}
                onChange={e => setForm({ ...form, contractType: e.target.value })}
              >
                <option value="CDI">CDI</option>
                <option value="CDD">CDD (période obligatoire)</option>
                <option value="INTERIM">Intérimaire (période obligatoire — AUCUN mail contrat)</option>
              </select>
            </div>
            {form.contractType !== 'CDI' && (
              <div className="form-row" style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label>Début contrat *</label>
                  <input type="date" value={form.contractStart}
                    onChange={e => setForm({ ...form, contractStart: e.target.value })} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label>Fin contrat *</label>
                  <input type="date" value={form.contractEnd}
                    onChange={e => setForm({ ...form, contractEnd: e.target.value })} required />
                </div>
              </div>
            )}
            {form.contractType === 'INTERIM' && (
              <div className="form-row" style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px' }}>
                Intérimaire : visible en Présence sur sa période, mais <strong>aucun mail contrat</strong> n'est envoyé
                (contrat géré par l'agence d'intérim) et pas de crédit JFR/férié.
              </div>
            )}
            {form.contractType === 'CDD' && (
              <>
                {/* Dossier SD Worx (30-09-2026, demande Aurélie) — ces champs partent dans
                    le mail contrats DANS L'ORDRE de l'écran SD Worx, pour qu'elle ne les
                    recopie plus depuis la photo de la carte d'identité. */}
                <div className="form-row" style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label>N° carte d'identité</label>
                    <input type="text" value={form.idCardNumber} maxLength={40} placeholder="592-1234567-89"
                      onChange={e => setForm({ ...form, idCardNumber: e.target.value })} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label>N° NISS <small style={{ color: '#94a3b8' }}>(11 chiffres)</small></label>
                    <input type="text" value={form.niss} maxLength={20} placeholder="91020374657"
                      onChange={e => setForm({ ...form, niss: e.target.value })} />
                  </div>
                </div>
                <div className="form-row" style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label>Nationalité</label>
                    <input type="text" value={form.nationality} maxLength={60} placeholder="Belge"
                      onChange={e => setForm({ ...form, nationality: e.target.value })} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label>Sexe</label>
                    <select value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                      <option value="">—</option><option value="F">Femme</option><option value="M">Homme</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label>Langue</label>
                    <select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
                      <option value="">—</option><option value="FR">FR</option><option value="NL">NL</option><option value="EN">EN</option>
                    </select>
                  </div>
                </div>
                <div className="form-row" style={{ display: 'flex', gap: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={form.disabledWorker === true || form.disabledWorker === 'true'}
                      onChange={e => setForm({ ...form, disabledWorker: e.target.checked })} />
                    Travailleur handicapé
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={form.survivorPension === true || form.survivorPension === 'true'}
                      onChange={e => setForm({ ...form, survivorPension: e.target.checked })} />
                    Pension de survie
                  </label>
                </div>
                <div className="form-row">
                  <label>Adresse domicile <small style={{ color: '#94a3b8' }}>(dossier contrat)</small></label>
                  <input type="text" value={form.address} maxLength={300}
                    onChange={e => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="form-row">
                  <label>IBAN <small style={{ color: '#94a3b8' }}>(dossier contrat)</small></label>
                  <input type="text" value={form.iban} maxLength={40} placeholder="BE.."
                    onChange={e => setForm({ ...form, iban: e.target.value })} />
                </div>
                <div className="form-row">
                  <label>Lieu de naissance <small style={{ color: '#94a3b8' }}>(dossier contrat)</small></label>
                  <input type="text" value={form.birthPlace} maxLength={150} placeholder="Ville, pays"
                    onChange={e => setForm({ ...form, birthPlace: e.target.value })} />
                </div>
                {/* Compléments dossier (19-08-2026, demande Florent) — pers. à charge + état civil */}
                <div className="form-row" style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label>Pers. à charge <small style={{ color: '#94a3b8' }}>(enfants, handicapés, seniors)</small></label>
                    <input type="number" min={0} max={20} value={form.dependentsCount}
                      onChange={e => setForm({ ...form, dependentsCount: e.target.value })} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label>État civil <small style={{ color: '#94a3b8' }}>(dossier contrat)</small></label>
                    <select value={form.maritalStatus} onChange={e => setForm({ ...form, maritalStatus: e.target.value })}>
                      <option value="">—</option>
                      <option value="MARIE">Marié(e)</option>
                      <option value="COHABITANT_LEGAL">Cohabitant légal</option>
                      <option value="CELIBATAIRE">Célibataire</option>
                    </select>
                  </div>
                </div>
                {(form.maritalStatus === 'MARIE' || form.maritalStatus === 'COHABITANT_LEGAL') && (
                  <div className="form-row" style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 2 }}>
                      <label>Nom du conjoint</label>
                      <input type="text" value={form.spouseName} maxLength={150}
                        onChange={e => setForm({ ...form, spouseName: e.target.value })} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label>Conjoint travaille ? <small style={{ color: '#94a3b8' }}>(salaire)</small></label>
                      <select value={String(form.spouseWorks)} onChange={e => setForm({ ...form, spouseWorks: e.target.value === '' ? '' : e.target.value })}>
                        <option value="">—</option>
                        <option value="true">Oui</option>
                        <option value="false">Non</option>
                      </select>
                    </div>
                  </div>
                )}
                {/* SD Worx demande aussi la date de naissance et le revenu du conjoint
                    (Aurélie 30-09-2026) — on n'avait que « travaille oui/non ». */}
                {(form.maritalStatus === 'MARIE' || form.maritalStatus === 'COHABITANT_LEGAL') && (
                  <div className="form-row" style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label>Date de naissance du conjoint</label>
                      <input type="date" value={form.spouseBirthDate}
                        onChange={e => setForm({ ...form, spouseBirthDate: e.target.value })} />
                    </div>
                    <div style={{ flex: 2 }}>
                      <label>Revenu du conjoint <small style={{ color: '#94a3b8' }}>(si salarié)</small></label>
                      <input type="text" value={form.spouseIncome} maxLength={100} placeholder="ex. 2 100 € net / mois"
                        onChange={e => setForm({ ...form, spouseIncome: e.target.value })} />
                    </div>
                  </div>
                )}
                {/* Carte d'identité recto/verso (19-08-2026) — jointe automatiquement au mail contrats */}
                <div className="form-row" style={{ display: 'flex', gap: 10 }}>
                  {[['recto', ciRecto, setCiRecto, user?.ciRectoPath], ['verso', ciVerso, setCiVerso, user?.ciVersoPath]].map(([side, val, setter, existing]) => (
                    <div key={side} style={{ flex: 1 }}>
                      <label>Carte ID {side} <small style={{ color: '#94a3b8' }}>(photo/PDF, max 10MB)</small></label>
                      <input type="file" accept="image/jpeg,image/png,application/pdf"
                        onChange={e => setter(e.target.files?.[0] || null)} />
                      {existing && !val && (
                        <small style={{ color: '#16a34a' }}>
                          ✓ déjà chargée — <a href={usersApi.ciUrl(user.id, side)} target="_blank" rel="noreferrer">voir</a> (choisir un fichier = remplacer)
                        </small>
                      )}
                      {val && <small style={{ color: '#2563eb' }}>→ {val.name}</small>}
                    </div>
                  ))}
                </div>
                {/* 30-09-2026 : visible aussi en ÉDITION — une prolongation part
                    désormais en demande SD Worx, le motif doit pouvoir l'accompagner. */}
                {/* 30-09-2026 — deux portes envoyaient la même demande à Aurélie. Celle-ci
                    est désormais fermée par défaut ; « Invitations › Collaborateur connu »
                    reste le chemin normal (il vérifie le dossier, prévient des fériés,
                    envoie à la personne sa confirmation + le guide, et laisse une trace). */}
                {isEdit && (
                <div className="form-row" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" style={{ marginTop: 3 }}
                      checked={form.sendContractRequest === true}
                      onChange={e => setForm({ ...form, sendContractRequest: e.target.checked })} />
                    <span>
                      Envoyer la demande de contrat à l'équipe contrats{' '}
                      <small style={{ color: '#94a3b8', display: 'block', fontWeight: 400 }}>
                        À cocher seulement si la demande n'est pas déjà partie depuis
                        « Invitations › Collaborateur connu ». Sans coche, la fiche est
                        enregistrée normalement et l'historique des contrats est tenu à jour,
                        mais aucun mail ne part.
                      </small>
                    </span>
                  </label>
                </div>
                )}
                <div className="form-row">
                  <label>
                    Motif de la demande{' '}
                    <small style={{ color: '#94a3b8' }}>
                      (optionnel — apparaît dans le mail contrats{isEdit ? ', si vous cochez ci-dessus' : ''})
                    </small>
                  </label>
                  <input type="text" value={form.motif} maxLength={300}
                    onChange={e => setForm({ ...form, motif: e.target.value })} />
                </div>
              </>
            )}

            {!isEdit && (
              <div className="form-row">
                <label>Mot de passe initial</label>
                <input
                  type="text"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                />
                <small>Par défaut: test1234</small>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =====================================================================
// Modal Conflict 409 - 3 optiuni (Refresh / Suprascrie / Anuleaza)
// =====================================================================
function ConflictModal({ data, onClose, onResolved }) {
  const handleOverwrite = async () => {
    try {
      // Conflit sur « Activer / Désactiver » : on applique l'état VOULU (pas une
      // fiche vide). Déjà dans cet état côté serveur → rien à faire.
      if (data.type === 'toggle') {
        if (!data.serverData?.updatedAt) { alert('Version du serveur inconnue : rechargez la page puis recommencez.'); onResolved(); return; }
        const voulu = !data.user.isActive;
        if (data.serverData?.isActive !== voulu) await usersApi.toggleActive(data.user.id, data.serverData.updatedAt);
        onResolved();
        return;
      }
      if (!data.serverData?.updatedAt) { alert('Version du serveur inconnue : rechargez la page puis recommencez.'); onResolved(); return; }
      // Foloseste expectedUpdatedAt de la server (mai noua)
      const payload = {
        ...data.myChanges,
        expectedUpdatedAt: data.serverData.updatedAt,
      };
      const updated = await usersApi.update(data.user.id, payload);
      if (updated?.warning) alert(updated.warning);
      onResolved();
    } catch (e) {
      alert('Échec de la sauvegarde: ' + (e.response?.data?.message || e.message));
    }
  };

  const formatDate = (d) => {
    if (!d) return '?';
    return new Date(d).toLocaleString('fr-BE');
  };

  return (
    <div className="modal-backdrop">
      <div className="modal modal-conflict">
        <div className="modal-header">
          <h2>⚠️ Conflit de modification</h2>
        </div>
        <div className="modal-body">
          <p>
            <strong>{data.user?.name}</strong> a été modifié par un autre administrateur 
            depuis que vous avez chargé la page.
          </p>
          <div className="conflict-info">
            <div>Votre version: <strong>{formatDate(data.user?.updatedAt)}</strong></div>
            <div>Version serveur: <strong>{formatDate(data.serverData?.updatedAt)}</strong></div>
          </div>
          <p>Que souhaitez-vous faire ?</p>
        </div>
        <div className="modal-footer modal-footer-conflict">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button className="btn btn-secondary" onClick={onResolved}>
            Recharger les données
          </button>
          <button className="btn btn-danger" onClick={handleOverwrite}>
            Écraser quand même
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Day 9d — Password Reset Result Modal
// =====================================================================
// Shows the temp password(s) ONCE so admin can communicate them to users.
function PasswordResetResultModal({ data, onClose }) {
  const single = data.single;
  const bulk = data.bulk;
  const list = single ? [single] : (bulk?.results || []);

  async function copyAllToClipboard() {
    const text = list.map((r) => `${r.userName} (${r.username}) : ${r.tempPassword}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      alert('Liste copiée dans le presse-papier !');
    } catch { alert('Copie échouée. Sélectionnez manuellement.'); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 10, maxWidth: 720, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', background: '#16a34a', color: 'white' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'white' }}>🔑 Mots de passe temporaires</h3>
          <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>
            {bulk ? `${bulk.resetCount} utilisateur(s) réinitialisé(s)` : `Utilisateur : ${single.userName}`}
          </div>
        </div>

        <div style={{ padding: '12px 20px', background: '#fffbeb', borderBottom: '1px solid #fde68a', color: '#78350f', fontSize: 12 }}>
          ⚠ <strong>Important :</strong> Cette liste n'est visible qu'une seule fois. Copiez-la ou notez-la maintenant pour communiquer aux utilisateurs. Ils devront changer leur mot de passe à la prochaine connexion.
        </div>

        {/* Comptes ÉCARTÉS (01-10-2026). Le serveur ne réinitialise plus les
            comptes plus puissants que l'auteur : il les SAUTE, au lieu de faire
            échouer toute l'opération pour un seul. Sans cet encadré, l'écran
            annonçait « N utilisateur(s) réinitialisé(s) » en vert et rien
            d'autre — l'opérateur distribuait la liste en croyant tout le monde
            servi, et les comptes sautés gardaient leur ancien mot de passe sans
            que rien ne le dise. Les NOMS, pas seulement le nombre : « pourquoi
            celui-là n'a-t-il pas reçu le sien ? » doit avoir une réponse à
            l'écran. */}
        {bulk?.skipped?.length > 0 && (
          <div style={{ padding: '12px 20px', background: '#fef2f2', borderBottom: '1px solid #fca5a5', color: '#991b1b', fontSize: 12 }}>
            <strong>{bulk.skipped.length} compte(s) n'ont PAS été réinitialisés</strong> : ils ont des droits que vous n'avez pas —{' '}
            {bulk.skipped.join(', ')}. Demandez à quelqu'un d'autre de s'en charger.
          </div>
        )}

        <div style={{ overflowY: 'auto', flex: 1, padding: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #cbd5e1' }}>
                <th style={{ padding: 8, textAlign: 'left' }}>Utilisateur</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Username</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Mot de passe temporaire</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.userId || r.username} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: 8 }}>{r.userName || r.name}</td>
                  <td style={{ padding: 8, color: '#64748b' }}>{r.username}</td>
                  <td style={{ padding: 8 }}>
                    <code style={{ fontFamily: 'monospace', background: '#fef3c7', color: '#78350f', padding: '4px 10px', borderRadius: 4, fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>
                      {r.tempPassword}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#64748b' }}>Ne s'affichera plus après fermeture.</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {list.length > 1 && (
              <button onClick={copyAllToClipboard} style={{ padding: '6px 14px', background: 'white', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                📋 Copier la liste
              </button>
            )}
            <button onClick={onClose} style={{ padding: '6px 14px', background: '#4f46e5', color: 'white', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
